import { Injectable } from '@angular/core';
import {
	PluginType,
	SequenceOrderingGameData,
	SequenceItem,
} from '@meta-press/shared-plugins';
import {
	GenerationRequest,
	GenerationResult,
	GenerationPrompts,
	ValidationResult,
} from './base/game-generation.interface';
import { BaseGenerationService } from './base/base-generation.service';
import { SettingsService } from '../settings-service/settings.service';

/**
 * Sequence ordering specific generation service
 */
@Injectable({
	providedIn: 'root',
})
export class SequenceOrderingGenerationService extends BaseGenerationService<SequenceOrderingGameData> {
	readonly pluginType = PluginType.SequenceOrdering;

	/**
	 * Create system and user prompts for sequence ordering generation
	 * @param request The generation request
	 * @param settingsService Settings service for language preferences
	 * @returns System and user prompts
	 */
	createPrompts(
		request: GenerationRequest,
		settingsService: SettingsService,
	): GenerationPrompts {
		const { parameters, sourceMaterial } = request;

		let systemPrompt: string;
		let userPrompt: string;

		// Get target language prompt from settings service
		const targetLanguagePrompt = settingsService.GetTargetLanguagePrompt();

		if (parameters['questionTopic']?.trim()) {
			// Topic-based generation
			systemPrompt = `Olet järjestelypelin suunnittelija. Tehtäväsi on luoda järjestelypeli, jossa käyttäjän pitää järjestää vaiheet oikeaan järjestykseen. Luo vaiheet, jotka ovat selkeitä ja yksinkertaisia, mutta myös tarpeeksi haastavia. Vastaa aina 

${targetLanguagePrompt}`;

			userPrompt = `Luo ${parameters['numberOfQuestions'] || 5} vaihetta aiheesta: "${parameters['questionTopic']}" jotka täytyy järjestää oikeaan järjestykseen.

Noudata seuraavia ohjeita:
- Luo looginen järjestys vaiheille
- Varmista, että vaiheet ovat selkeitä ja ymmärrettäviä. Vaiheen tulee olla ytimekäs ja lyhyt lause.
- Vaiheet voivat olla prosessi, aikajärjestys, tai looginen järjestys
- ÄLÄ sisällytä vuosilukuja, päivämääriä, numeroita tai muita järjestysvihjeitä teksteihin, jotka tekisivät pelistä liian helpon
- Luo selitys oikealle järjestykselle

Vastaa JSON-muodossa:
{
    "title": "Järjestelypelin otsikko",
    "description": "Järjestelypelin kuvaus",
    "items": [
        {
            "id": 1,
            "text": "Ensimmäinen vaihe"
        },
        {
            "id": 2, 
            "text": "Toinen vaihe"
        }
    ],
    "explanationText": "Teksti, kun käyttäjä on järjestänyt vaiheet oikein"
}

Varmista, että items-taulukon id-kenttä kuvaa vaiheiden oikeaa järjestystä: 1 on ensimmäinen vaihe, 2 on toinen jne.`;
		} else {
			// Source material based generation
			systemPrompt = `Olet järjestelypelin suunnittelija. Tehtäväsi on luoda järjestelypeli annetusta lähdemateriaalista, jossa käyttäjän pitää järjestää vaiheet oikeaan järjestykseen. Selvitä, mistä lähdemateriaalissa on kyse ja luo siitä looginen vaiheistus.

${targetLanguagePrompt}`;

			const includeImages = sourceMaterial?.imagesAsString
				? `\n\nLähdemateriaalissa on myös kuvia. Käytä kuvia apuna vaiheiden laatimisessa:\n${sourceMaterial.imagesAsString}`
				: '';

			userPrompt = `Luo ${parameters['numberOfQuestions'] || 5} vaihetta valitusta lähdemateriaalista, jotka täytyy järjestää oikeaan järjestykseen.${includeImages}

Noudata seuraavia ohjeita:
- Perusta vaiheet lähdemateriaalin sisältöön
- Luo looginen prosessi, aikajärjestys, tai kehitysjärjestys
- Varmista, että vaiheet ovat selkeitä ja faktaperusteisia. Tekstin tulee olla ytimekäs ja lyhyt lause.
- Järjestä vaiheet loogiseen järjestykseen
- ÄLÄ sisällytä vuosilukuja, päivämääriä, numeroita tai muita järjestysvihjeitä teksteihin, jotka tekisivät pelistä liian helpon
- Jos käytät kuvia, älä puhu kuvasta vaiheen tekstikuvauksessa. Kuvan id tulee laittaa imageId-kenttään.

Vastaa JSON-muodossa:
{
    "title": "Järjestelypelin otsikko", 
    "description": "Järjestelypelin kuvaus",
    "items": [
        {
            "id": 1,
            "text": "Ensimmäinen vaihe",
			"imageId": -1 // oletuksena -1. Jos tähän vaiheeseen liittyy jokin lähdemateriaalin kuvista, laita tähän kuvan id. Kuvan id on json-muodossa annetuissa kuvissa esiintyvä id-kenttä. Jätä muuten arvoksi -1.
        },
        {
            "id": 2,
            "text": "Toinen vaihe",
			"imageId": -1 // oletuksena -1. Jos tähän vaiheeseen liittyy jokin lähdemateriaalin kuvista, laita tähän kuvan id. Kuvan id on sinulle json-muodossa annetuissa kuvissa esiintyvä id-kenttä. Jätä muuten arvoksi -1.
        }
    ],
    "explanationText": "Teksti, kun käyttäjä on järjestänyt vaiheet oikein"
}

Varmista, että items-taulukon id-kenttä kuvaa vaiheiden oikeaa järjestystä: 1 on ensimmäinen vaihe, 2 on toinen jne.`;
		}

		return { system: systemPrompt, user: userPrompt };
	}

	/**
	 * Process AI response into SequenceOrderingGameData
	 * @param response The AI service response
	 * @param parameters Original generation parameters
	 * @returns Generation result with game data or error
	 */
	protected processResponseCore(
		response: any,
		parameters: any,
	): GenerationResult {
		try {
			const generatedData = response.json;

			if (!generatedData) {
				return {
					success: false,
					error: 'Invalid response format: no data found',
					rawResponse: response.rawResponse,
				};
			}

			// Validate structure
			if (
				!generatedData.items ||
				!Array.isArray(generatedData.items) ||
				generatedData.items.length === 0
			) {
				return {
					success: false,
					error: 'Invalid response format: no sequence steps found',
					rawResponse: response.rawResponse,
				};
			}

			// Validate sequence items
			for (const item of generatedData.items) {
				if (!item.text || typeof item.id !== 'number') {
					return {
						success: false,
						error: 'Invalid sequence step structure in response',
						rawResponse: response.rawResponse,
					};
				}
			}

			// Create game data
			const gameData = new SequenceOrderingGameData();
			gameData.title = generatedData.title || 'Järjestelypeli';
			gameData.description =
				generatedData.description ||
				'Raahaa ja pudota vaiheet oikeaan järjestykseen.';
			gameData.explanationText =
				generatedData.explanationText ||
				'Oikein! Vaiheet ovat oikeassa järjestyksessä.';

			// Convert items to SequenceItem format
			gameData.items = generatedData.items.map(
				(item: any) =>
					({
						id: item.id,
						text: item.text,
						imageId: item.imageId || -1,
					}) as SequenceItem,
			);

			return {
				success: true,
				gameData,
				rawResponse: response.rawResponse,
			};
		} catch (error) {
			return {
				success: false,
				error: `Response processing failed: ${error}`,
				rawResponse: response.rawResponse,
			};
		}
	}

	/**
	 * Validate sequence ordering specific request
	 */
	validateRequest(request: GenerationRequest): ValidationResult {
		const hasSourceMaterial =
			request.sourceMaterial &&
			request.sourceMaterial.fileHandles.length > 0;
		const hasTopic =
			request.parameters['questionTopic'] &&
			request.parameters['questionTopic'].trim();

		if (!hasSourceMaterial && !hasTopic) {
			return {
				valid: false,
				error: 'Syötä aihe vaiheille tai valitse lähdemateriaali',
			};
		}

		return { valid: true };
	}

	/**
	 * Get default parameters for sequence ordering
	 */
	getDefaultParameters(): any {
		return {
			numberOfQuestions: 4,
			clearPrevious: true,
		};
	}

	/**
	 * Merge new sequence steps with existing game data
	 * @param existingGameData Current game data
	 * @param newGameData Generated game data
	 * @param clearPrevious Whether to clear existing steps
	 * @returns Updated game data
	 */
	override mergeContent(
		existingGameData: SequenceOrderingGameData,
		newGameData: SequenceOrderingGameData,
		clearPrevious: boolean,
	): SequenceOrderingGameData {
		const newSteps = newGameData.items || [];

		if (clearPrevious) {
			existingGameData.items = newSteps;
		} else {
			// If first step is empty placeholder, remove it
			if (
				existingGameData.items.length === 1 &&
				(!existingGameData.items[0].text ||
					existingGameData.items[0].text === 'Vaihe 1')
			) {
				existingGameData.items = [];
			}

			// Add new steps to existing ones, renumbering the IDs
			const startId =
				existingGameData.items.length > 0
					? Math.max(
							...existingGameData.items.map((item) => item.id),
						) + 1
					: 1;

			const renumberedSteps = newSteps.map((step, index) => ({
				...step,
				id: startId + index,
			}));

			existingGameData.items.push(...renumberedSteps);
		}

		return existingGameData;
	}
}
